import { I18nThemed } from 'interfaces/mixins';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { v, w } from '@dojo/framework/core/vdom';
import { DNode } from '@dojo/framework/core/interfaces';
import { Store } from '@dojo/framework/stores/Store';
import { storeInject } from '@dojo/framework/stores/StoreInjector';
import { UserMeta } from 'lcc-state';

import Tooltip, { Orientation } from '@dojo/widgets/tooltip';
import { publish, subscribe, Handle } from 'pub-sub';
import { userstore } from 'lcc-state';
import { ToolState } from 'lcc-state/interfaces';

import css from '../css/lptool.m.css';

export interface LPToolProperties {
	classes?: string[];
	name: string;
	element?: string;
	icon?: string;
	iconcomplete?: string;
	tooltip?: string;
	action?: string;
	toolstate?: ToolState;
	location?: string;
	disablewhen?: string;
	enablewhen?: string;
}

@theme(css)
@storeInject({
	name: 'user-state',
	paths: [['toolstate'], ['location']],
	getProperties: (store: Store<UserMeta>) => {
		const { get, path } = store;
		return {
			toolstate: get(path('toolstate')),
			location: get(path('location'))
		} as LPToolProperties;
	}
})
export class LPTool extends I18nThemed<LPToolProperties> {
	private _tooltipShowing: boolean = false;
	private _disabled: boolean = false;
	private _inited: boolean = false;
	private _subs: Handle[] = [];

	onDetach(): void {
		this._subs.forEach((h) => {
			h.destroy();
		});
	}

	protected render(): DNode {
		const {
			classes = [],
			name,
			element = 'li',
			icon = '',
			iconcomplete,
			tooltip,
			action,
			location = '',
			disablewhen,
			enablewhen
		} = this.properties;
		const { get, path } = userstore;
		const {
			complete = false,
			extra,
			hide
		} = get(path('toolstate', action as string)) || {
			complete: false,
			extra: undefined,
			hide: undefined
		};
		const { _tooltipShowing, _disabled } = this;
		let useicon = complete && iconcomplete ? iconcomplete : icon;

		// handle special case for countdown
		if (useicon === 'clock' && hide) {
			useicon = 'countdown';
		}

		const svgpath = `${window.hcstatic.portal_cdn}/js/images/iconsheet.svg#${useicon}`;
		const matches = location.match(/^\/?([\w-]*)/) || [];
		const active = action === matches[1];

		if (!this._inited) {
			disablewhen &&
				this._subs.push(
					subscribe(disablewhen, () => {
						this._disabled = true;
						this.invalidate();
					})
				);
			enablewhen &&
				this._subs.push(
					subscribe(enablewhen, () => {
						this._disabled = false;
						this.invalidate();
					})
				);
			this._inited = true;
		}

		return v(
			element,
			{
				key: name,
				role: 'menuitem',
				tabIndex: 0,
				classes: [
					this.theme(css.tool),
					complete ? this.theme(css.completed) : '',
					active ? this.theme(css.active) : _disabled ? this.theme(css.disabled) : '',
					...classes
				],
				xqa: `lptool ${name}`,
				'aria-label': tooltip,
				onclick: (evt) => {
					evt && evt.stopPropagation();
					if (!_disabled) {
						const act = `site/${action || name}`;
						publish(act);
					}
				},
				onmouseover: () => {
					if (!this._tooltipShowing) {
						this._tooltipShowing = true;
						this.invalidate();
					}
				},
				onmouseout: () => {
					this._tooltipShowing = false;
					this.invalidate();
				},
				onfocus: () => {
					//
				}
			},
			[
				w(
					Tooltip,
					{
						key: `${name}-tt`,
						orientation: Orientation.bottom,
						open: _tooltipShowing
					},
					[
						{
							content: tooltip,
							trigger: [
								v('svg', { 'xmlns:xlink': 'http://www.w3.org/1999/xlink', role: 'img' }, [
									v('use', { href: svgpath, 'xlink:href': svgpath })
								]),
								complete ? v('div', { classes: [this.theme(css.complete)] }, ['\u2713']) : null,
								extra && !hide ? v('div', { classes: [this.theme(css.extra)] }, [extra]) : null
							]
						}
					]
				)
			]
		);
	}
}

export default LPTool;
